'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/navigation/switcher/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { Menu } from 'BomdisoftComponents/menu/index';
import { type ColorVariant } from 'BomdisoftComponents/types';
import { type ReactNode, useId, useRef, useState } from 'react';

export type SwitcherProps = {
  /** The current selection, rendered as the trigger's text. */
  label: string;
  /** Menu contents. Use `MenuItem` and `MenuDivider`. */
  children: ReactNode;
  /** Shows a status dot before the label, tinted by the variant. */
  status?: ColorVariant;
  /** A leading glyph or image before the label: a workspace or tenant avatar. Sized by a fixed
   * slot, so it cannot change the trigger's height next to a switcher that has none. */
  icon?: ReactNode;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  testId?: string;
};

const Caret = () => (
  <svg aria-hidden='true' className='bomdisoft-switcher-caret' fill='none' stroke='currentColor' strokeWidth='1.6' viewBox='0 0 12 12'>
    <path d='M3 4.5L6 7.5L9 4.5' />
  </svg>
);

/**
 * A top-bar control that shows the current selection and opens a `Menu` to
 * change it: the tenant, project or environment switcher every console needs.
 *
 * Sized as a chip rather than a button because it sits in the bar rather than
 * in content, and every chip in a bar has to share one box: the status dot is a
 * fixed element so it can never change the trigger's height next to a switcher
 * that has none.
 */
export const Switcher = ({ label, children, status, icon, ariaLabel, className, disabled, testId }: SwitcherProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  return (
    <>
      <button
        aria-expanded={open}
        aria-haspopup='menu'
        aria-label={ariaLabel ?? label}
        className={joinClasses('bomdisoft-switcher', className)}
        data-open={open ? '' : undefined}
        data-status={status}
        data-testid={testId}
        disabled={disabled}
        id={id}
        onClick={() => setOpen(current => !current)}
        ref={anchorRef}
        type='button'
      >
        {icon ? (
          <span aria-hidden='true' className='bomdisoft-switcher-icon'>
            {icon}
          </span>
        ) : null}
        {status ? <span aria-hidden='true' className='bomdisoft-switcher-dot' /> : null}
        <span className='bomdisoft-switcher-label'>{label}</span>
        <Caret />
      </button>
      <Menu anchorRef={anchorRef} onClose={() => setOpen(false)} open={open} testId={testId ? `${testId}-menu` : undefined}>
        {children}
      </Menu>
    </>
  );
};
